/**
 * General error handling utilities.
 *
 * Use this module for:
 * - Extracting error messages from unknown error types (getErrorMessage)
 * - Safe JSON serialization with circular reference handling (safeJsonStringify)
 * - Generic API error handling with logging (handleApiError)
 *
 * For auth-specific error handling (401/403 classification, AuthError types),
 * use `lib/apiErrorHandling.ts` instead.
 *
 * @module utils/errorUtils
 */

/**
 * Extract error message from an error object.
 * Handles various error types and formats consistently.
 * @param error - Error object (Error, string, unknown, etc.)
 * @returns Human-readable error message
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unknown error occurred';
}

/**
 * Handle API errors consistently across components.
 * Extracts error message and logs if needed.
 * @param error - Error object from API call
 * @param context - Context string for logging (e.g., 'sendMessage', 'editMessage')
 * @param logger - Optional logger function for logging errors
 * @returns Error message string
 */
export function handleApiError(
  error: unknown,
  context: string,
  logger?: (message: string, details?: Record<string, unknown>) => void
): string {
  const message = getErrorMessage(error);
  
  if (logger) {
    logger(`Failed to ${context}`, {
      error: message,
      stack: error instanceof Error ? error.stack : undefined,
    });
  }
  
  return message;
}

/**
 * JSON stringify replacer that handles circular references.
 * @param _key - JSON key (unused)
 * @param value - Value to check
 * @param seen - WeakSet tracking seen objects
 * @returns Value or '[Circular]' marker
 */
function createCircularReplacer(seen: WeakSet<object>) {
  return (_key: string, value: unknown): unknown => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  };
}

/**
 * Safely serialize a value to JSON string, handling circular references.
 * @param value - Value to serialize
 * @returns JSON string or fallback string representation
 */
export function safeJsonStringify(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Error) {
    return `${value.name}: ${value.message}${value.stack ? '\n' + value.stack : ''}`;
  }
  try {
    const seen = new WeakSet<object>();
    return JSON.stringify(value, createCircularReplacer(seen));
  } catch {
    return String(value);
  }
}

/**
 * Safely serialize a value to a JSON-compatible object, handling circular references.
 * @param value - Value to serialize
 * @returns Serializable object or undefined
 */
export function safeJsonSerialize(value: unknown): unknown {
  if (value instanceof Error) {
    return {
      type: 'Error',
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  if (typeof value === 'object' && value !== null) {
    try {
      const seen = new WeakSet<object>();
      return JSON.parse(JSON.stringify(value, createCircularReplacer(seen)));
    } catch {
      return String(value);
    }
  }
  return value;
}

/**
 * Extract URL string from fetch input.
 * @param input - Fetch input (string, URL, or Request)
 * @returns URL string
 */
export function extractFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (input instanceof Request) return input.url;
  return String(input);
}

